import { Inject, Injectable, Renderer2 } from '@angular/core';
import { addClass, removeClass } from '../../../utils/toggle-class/toggle-class';
import { modalVars } from '../../../constants/modal-vars';
import { elementsContainer, modalContainer } from 'projects/lime-main/src/app/constants/modal-elements.constants';
import { DOCUMENT } from '@angular/common';
import { NativeAppService } from '@shared/common/services/native-app/native-app.service';

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  modals: HTMLElement[] = [];
  modalVars = modalVars;
  renderer: Renderer2;
  scrollY: number;
  modalStack: { modal: HTMLElement; isIphoneModalFix: boolean }[] = [];

  get isElements(): boolean {
    return !!(window as any).__isAngularElement__;
  }

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly nativeAppService: NativeAppService
  ) {}

  add(modal: any) {
    this.modals.push(modal);
  }

  remove(id: string) {
    this.modals = this.modals.filter((x) => x.id !== id);
    this.modalStack = this.modalStack.filter((m) => m.modal.id !== id);
  }

  recalculateZIndexes() {
    const BACKGROUND_BASE_Z_INDEX = 900;
    const CONTENT_BASE_Z_INDEX = 1000;
    const Z_STEP = 150;

    this.modals.forEach((modal, index) => {
      if (!modal) return;

      const background: HTMLElement = modal.querySelector('.lm-modal__background');
      const content: HTMLElement = modal.querySelector('.lm-modal__container');

      if (background) background.style.zIndex = String(BACKGROUND_BASE_Z_INDEX + index * Z_STEP);
      if (content) content.style.zIndex = String(CONTENT_BASE_Z_INDEX + index * Z_STEP);
    });
  }

  open(id: string, isIphoneModalFix?: boolean) {
    const isIOS = this.getIOSVersion();
    const isSafari = this.getBrowserName();
    this.scrollY = window.scrollY;

    this.modalVars[id] = true;

    setTimeout(() => {
      const modalToOpen = this.modals.find((x) => x.id === id);
      if (!modalToOpen) return;

      this.modalStack.push({ modal: modalToOpen, isIphoneModalFix });

      let parent = document.body;
      this.nativeAppService.notifyIntentToOpenBottomSheet(id);

      // in the elements environment, we're rendering the modal inside of the shadow dom
      // this is necessary for the styles to be applied to the modal
      if (this.isElements) {
        parent = document.querySelector(elementsContainer)?.shadowRoot?.querySelector(modalContainer);
      }

      this.renderer.appendChild(parent, modalToOpen);
      modalToOpen.style.display = 'block';

      this.recalculateZIndexes();

      // need it to show a safari bottom menu bar for version of ios < 15
      if (isIphoneModalFix && isIOS && isIOS < 15 && isSafari) {
        addClass('lm-modal-open--fixed', document.body);
        document.body.style.top = this.scrollY * -1 + 'px';
        return;
      }

      addClass('lm-modal-open', document.body);
      // because of elements, we don't have our own styles on the body
      // so we're disabling scroll by adding these styles directly
      if (this.isElements) {
        document.body.style.cssText = `
            overflow: hidden;
          `;
      }
    }, 1);
  }

  close(id: string, isIphoneModalFix?: boolean) {
    const isIOS = this.getIOSVersion();
    const isSafari = this.getBrowserName();
    const modalToClose = this.modalStack.find((m) => m.modal.id === id);

    if (!modalToClose) return;

    this.modalStack = this.modalStack.filter((m) => m.modal.id !== id);
    const otherModalStack = this.modalStack.filter((m) => m.modal.id !== id);
    const otherModalsHasIsIphoneFix = otherModalStack.some((m) => m.isIphoneModalFix === true);

    this.nativeAppService.notifyIntentToCloseBottomSheet(id);
    setTimeout(() => {
      modalToClose.modal.style.display = 'none';

      this.recalculateZIndexes();

      // need it to show a safari bottom menu bar for version of ios < 15
      if (!otherModalsHasIsIphoneFix && isIphoneModalFix && isIOS && isIOS < 15 && isSafari) {
        document.body.style.top = '';
        removeClass('lm-modal-open--fixed', document.body);
        window.scroll(0, this.scrollY);

        return;
      }

      if (!otherModalStack.length) {
        removeClass('lm-modal-open', document.body);
        // because of elements, remove the previously
        // set styles on the body
        if (this.isElements) {
          document.body.style.cssText = '';
        }
      }
    }, 1);
  }

  closeAllModals() {
    setTimeout(() => {
      this.modals.map((modal) => {
        modal.style.display = 'none';
      });
      removeClass('lm-modal-open', document.body);
    }, 1);
  }

  setRender(render: Renderer2) {
    this.renderer = render;
  }

  getIOSVersion() {
    if (/iP(hone|od|ad)/.test(navigator.platform)) {
      let v = navigator.appVersion.match(/OS (\d+)_(\d+)_?(\d+)?/);
      return [parseInt(v[1], 10), parseInt(v[2], 10), parseInt(v[3], 10)][0];
    }
  }

  isIos() {
    const userAgent = window?.navigator?.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  }

  getBrowserName() {
    return navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') <= -1;
  }

  /**
   * @description
   * Sync Height of the HTML element on resize to prevent scrolling on iOS.
   * Resize happens when browser footer is shown / hidden on iOS browsers.
   */
  iOsDisableScrolling() {
    this.renderer.addClass(this.document.documentElement, 'scrolling-is-disabled');
    this.document.documentElement.style.setProperty('--window-inner-height', `${window.innerHeight}px`);
    return;
  }

  /**
   * @description
   * Enabling the scrolling when modal is closed. Removing special class and calculated height
   */
  iOsEnableScrolling() {
    this.renderer.removeClass(this.document.documentElement, 'scrolling-is-disabled');
    this.document.documentElement.style.removeProperty('--window-inner-height');
    return;
  }

  /**
   * @description
   * If modal is shown disable scrolling.
   * If received true then disable scrolling
   */
  handleIosScrolling(showModal: boolean) {
    if (!this.isIos()) {
      return;
    }

    if (showModal) {
      this.iOsDisableScrolling();
      return;
    }

    this.iOsEnableScrolling();
  }
}
